import { useRef } from 'react';
import { useEffect } from 'react';
import { usePrevious } from 'react-use';
import { useDebounce } from 'use-debounce';

interface SearchParams<Option> {
    inputValue: string;
    setOptions: (options: Option[]) => void;
    setMoreOptionsCount: (moreOptionsCount: number) => void;
    setLoading: (value: boolean) => void;
    searchOptions: (
        query: string
    ) => Promise<{ items: Option[]; moreOptionsCount: number }>;
}

export const useSearch = <Option>({
    inputValue,
    setOptions,
    setMoreOptionsCount,
    setLoading,
    searchOptions,
}: SearchParams<Option>) => {
    const cache = useRef(new Map());

    const [debouncedInputValue] = useDebounce(inputValue, 300, {
        leading: false,
        trailing: true,
    });

    const prevDebouncedInputValue = usePrevious(debouncedInputValue);

    useEffect(() => {
        async function fetchOptions() {
            if (cache.current.has(debouncedInputValue)) {
                const cachedOptions: Option[] =
                    cache.current.get(debouncedInputValue);
                setOptions(cachedOptions);
            } else {
                setLoading(true);
                const { items, moreOptionsCount } = await searchOptions(
                    debouncedInputValue
                );
                cache.current.set(debouncedInputValue, items);
                setOptions(items);
                setMoreOptionsCount(moreOptionsCount);
                setLoading(false);
            }
        }

        if (
            debouncedInputValue &&
            prevDebouncedInputValue !== debouncedInputValue
        ) {
            fetchOptions().catch(() => {
                setOptions([]);
                setMoreOptionsCount(0);
                setLoading(false);
            });
        }
    }, [
        debouncedInputValue,
        searchOptions,
        prevDebouncedInputValue,
        setOptions,
        setLoading,
        setMoreOptionsCount,
    ]);
};
